import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton, Select, Stack, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import GenericModal from 'components/GenericModal';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

function BuildingDemandTimeRangeDialogSelect({
  buttonTitle,
  color,
  default: defaultOption,
  dialogTitle,
  onSelectionOK,
  selectLabel,
  selectOptions = [],
  toolTip,
}) {
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(defaultOption || selectOptions[0]);
  const [tempSelectedOption, setTempSelectedOption] = useState(defaultOption || selectOptions[0]);
  const [selectedStartDate, setSelectedStartDate] = useState(DateTime.local().startOf('day'));
  const [selectedEndDate, setSelectedEndDate] = useState(DateTime.local().startOf('day'));

  useEffect(() => {
    if (!defaultOption) return;
    setSelectedOption(defaultOption);
    if (!open) {
      setTempSelectedOption(defaultOption);
    }
  }, [defaultOption, open]);

  const handleOpen = () => {
    const isCustomDateRange = selectedOption?.value && typeof selectedOption.value === 'object';
    setTempSelectedOption(isCustomDateRange ? selectOptions.find(option => option.value === 'DATE_TO_DATE') : selectedOption);
    if (isCustomDateRange) {
      setSelectedStartDate(DateTime.fromFormat(selectedOption.value.startDate, 'dd.MM.yyyy'));
      setSelectedEndDate(DateTime.fromFormat(selectedOption.value.endDate, 'dd.MM.yyyy'));
    }
    setOpen(true);
  };

  const handleClose = () => {
    setTempSelectedOption(selectedOption);
    setOpen(false);
  };

  const handleOK = () => {
    if (tempSelectedOption?.value === 'DATE_TO_DATE') {
      if (!selectedStartDate?.isValid || !selectedEndDate?.isValid) return;

      const dateRange = {
        label: `${selectedStartDate.toFormat('dd.MM.yyyy')} - ${selectedEndDate.toFormat('dd.MM.yyyy')}`,
        value: {
          startDate: selectedStartDate.toFormat('dd.MM.yyyy'),
          endDate: selectedEndDate.toFormat('dd.MM.yyyy'),
        },
      };
      setSelectedOption(dateRange);
      onSelectionOK(dateRange);
    } else {
      setSelectedOption(tempSelectedOption);
      onSelectionOK(tempSelectedOption);
    }
    setOpen(false);
  };

  const handleStartDateChange = date => {
    setSelectedStartDate(date);
    if (!date?.isValid) return;

    const maxEndDate = date.plus({ days: 6 });
    if (!selectedEndDate?.isValid || selectedEndDate < date || selectedEndDate > maxEndDate) {
      setSelectedEndDate(date);
    }
  };

  const dialogTitleNode = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{dialogTitle || ''}</Box>
      {toolTip && (
        <Tooltip title={toolTip} slotProps={{ popper: { sx: { zIndex: 50020 } } }}>
          <IconButton size="small" sx={{ color: 'grey', p: 0.5 }}>
            <InfoIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const DialogContent = () => (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Box component="form" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', width: '100%', gap: 1 }}>
        <FormControl sx={{ m: 1, minWidth: 120, flex: 1 }}>
          <InputLabel>{selectLabel || ''}</InputLabel>
          <Select
            native
            value={tempSelectedOption?.value || ''}
            onChange={event => setTempSelectedOption(selectOptions.find(option => String(option.value) === event.target.value))}
            input={<OutlinedInput label={selectLabel || ''} />}>
            {selectOptions.map(option => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>
        {tempSelectedOption?.value === 'DATE_TO_DATE' && (
          <>
            <DatePicker
              sx={{ m: 1, width: 180 }}
              label="Start date"
              value={selectedStartDate}
              onChange={handleStartDateChange}
              maxDate={DateTime.local().startOf('day')}
              format="dd.MM.yyyy"
              slots={{ textField: TextField }}
              slotProps={{ textField: { variant: 'outlined' }, popper: { sx: { zIndex: 50010 } } }}
            />
            <DatePicker
              sx={{ m: 1, width: 180 }}
              label="End date"
              value={selectedEndDate}
              onChange={setSelectedEndDate}
              minDate={selectedStartDate}
              maxDate={
                selectedStartDate ? DateTime.min(selectedStartDate.plus({ days: 6 }), DateTime.local().startOf('day')) : null
              }
              disabled={!selectedStartDate?.isValid}
              format="dd.MM.yyyy"
              slots={{ textField: TextField }}
              slotProps={{ textField: { variant: 'outlined' }, popper: { sx: { zIndex: 50010 } } }}
            />
          </>
        )}
        <IconButton
          aria-label="ok"
          onClick={handleOK}
          sx={{ mt: 1, '&:hover': { backgroundColor: 'rgba(225, 225, 225, 1)', color: 'black' } }}>
          <DoneIcon />
        </IconButton>
      </Box>
    </LocalizationProvider>
  );

  return (
    <>
      <MDButton color="white" variant="text" onClick={handleOpen}>
        <Stack>
          <MDTypography variant="button" fontWeight="bold" color={color}>
            {buttonTitle || ''}
          </MDTypography>
          <MDTypography className="bar-display-selected" variant="caption" fontWeight="light" color={color}>
            {selectedOption?.label || ''}
          </MDTypography>
        </Stack>
      </MDButton>
      <GenericModal
        open={open}
        onClose={handleClose}
        title={dialogTitleNode}
        Component={DialogContent}
        contentProps={{ dividers: true, sx: { p: 2, borderBottom: 'none !important' } }}
        closeOnBackdrop={true}
        closeOnEsc={true}
      />
    </>
  );
}

export default BuildingDemandTimeRangeDialogSelect;
